import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, IdCard } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import { validateIqama, sanitizeIqama } from '@/lib/iqama';

interface IqamaInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Smart Iqama / National ID input.
 *
 *   - Sanitizes: digits only, max 10
 *   - Live validation: 10 digits starting with 1 or 2
 *   - Shows badge: "Saudi National ID" if starts with 1, "Iqama" if starts with 2
 *   - Red error state for invalid input
 */
export function IqamaInput({ value, onChange, disabled = false, required = false }: IqamaInputProps) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  const validation = validateIqama(value);
  const showError = touched && !validation.valid && value.length > 0;

  const handleChange = (raw: string) => {
    onChange(sanitizeIqama(raw));
  };

  const fieldClass = `flex h-11 w-full rounded-md border bg-background px-3 py-2 text-base font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors ${
    showError
      ? 'border-destructive focus-visible:ring-destructive'
      : 'border-input focus-visible:ring-ring'
  }`;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>
          {t('drivers.field.iqamaNumber')} {required && <span className="text-destructive">*</span>}
        </Label>
        {validation.valid && validation.type && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${
            validation.type === 'national_id'
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-blue-600 dark:text-blue-400'
          }`}>
            <IdCard className="h-3 w-3" />
            {t(`drivers.iqamaType.${validation.type}`)}
          </span>
        )}
        {showError && (
          <span className="inline-flex items-center gap-1 text-[11px] text-destructive">
            <AlertCircle className="h-3 w-3" /> {t('plate.invalid')}
          </span>
        )}
        {validation.valid && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-3 w-3" />
            {t('plate.valid')}
          </span>
        )}
      </div>
      <input
        type="text"
        dir="ltr"
        inputMode="numeric"
        placeholder="2123456789"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        maxLength={10}
        className={`${fieldClass} tracking-widest`}
        aria-label={t('drivers.field.iqamaNumber')}
      />
      {showError && validation.error && (
        <p className="text-xs text-destructive flex items-start gap-1">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          {validation.error}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">
        {t('drivers.help.iqamaFormat')}
      </p>
    </div>
  );
}
